import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTrackContext } from "@/state/TrackContext";
import {
  applyPathwayCssVars,
  getPathwayStyling,
  getScreenStyle,
  getVisibilityConfig,
  resolvePathwayKey,
  typographyToCss,
} from "@/shared/utils/stylingConfigOperations";
import { PathwayKey, ScreenStyleKey, VisibilityConfig } from "@/types/stylingConfig";

/**
 * Resolves the active BK/HK pathway styling from theme config, applies CSS
 * variables, and exposes helpers for screens / visibility.
 */
export const usePathwayStyling = (screen?: ScreenStyleKey) => {
  const location = useLocation();
  const { trackSelectionOption } = useTrackContext();

  const pathway: PathwayKey = useMemo(
    () =>
      resolvePathwayKey({
        trackTitle: trackSelectionOption?.title,
        pathname: location.pathname,
      }),
    [trackSelectionOption?.title, location.pathname],
  );

  const pathwayStyle = useMemo(() => getPathwayStyling(pathway), [pathway]);
  const visibility: VisibilityConfig = useMemo(() => getVisibilityConfig(), []);
  const screenStyle = useMemo(
    () => (screen ? getScreenStyle(pathway, screen) : undefined),
    [pathway, screen],
  );

  useEffect(() => {
    applyPathwayCssVars(pathway);
  }, [pathway]);

  const screenVariablePrefix = screen ? `--booking-screen-${pathway}-${screen}` : undefined;
  const titleStyle = useMemo(
    () => typographyToCss(screenStyle?.title, screenVariablePrefix ? `${screenVariablePrefix}-title` : undefined),
    [screenStyle?.title, screenVariablePrefix],
  );
  const subtitleStyle = useMemo(
    () => typographyToCss(screenStyle?.subtitle, screenVariablePrefix ? `${screenVariablePrefix}-subtitle` : undefined),
    [screenStyle?.subtitle, screenVariablePrefix],
  );
  const screenBackground =
    screenVariablePrefix && screenStyle?.background
      ? `var(${screenVariablePrefix}-background, ${screenStyle.background})`
      : screenStyle?.background ?? pathwayStyle.screenBackground;

  return {
    pathway,
    isBattleKart: pathway === "bk",
    pathwayStyle,
    visibility,
    screenStyle,
    titleStyle,
    subtitleStyle,
    screenBackground,
  };
};
